/* HOTSTOP B90I — AUTOMATIC LOGGED-IN MEMBER DISCOUNT EXPERIENCE */
(() => {
  if (window.HotStopMemberDiscount) return;

  const root = document.querySelector('[data-hotstop-member-discount]');
  if (!(root instanceof HTMLElement)) {
    document.documentElement.dataset.hotstopB90iStatus = 'not-rendered';
    return;
  }

  const authenticated = root.dataset.authenticated === 'true';
  const discountPercent = root.dataset.discountPercent || '10';
  const popupDelay = Math.max(0, Number.parseInt(root.dataset.popupDelay || '600', 10) || 0);
  const confettiEnabled = root.dataset.confettiEnabled === 'true';
  const confettiDurationSeconds = Math.min(8, Math.max(1, Number.parseFloat(root.dataset.confettiDuration || '3') || 3));
  const confettiDurationMs = Math.round(confettiDurationSeconds * 1000);
  const overlay = root.querySelector('[data-hotstop-member-overlay]');
  const dialog = root.querySelector('[data-hotstop-member-dialog]');
  const loginLinks = root.querySelectorAll('[data-hotstop-member-login-link]');
  const openButtons = root.querySelectorAll('[data-hotstop-member-open]');
  const closeButtons = root.querySelectorAll('[data-hotstop-member-close], [data-hotstop-member-close-link]');

  const pendingKey = 'hotstopMemberLoginPending';
  const seenKey = 'hotstopMemberDiscountSeen';
  let lastFocusedElement = null;
  let confettiCleanupTimer = 0;

  const storage = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (_) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (_) {}
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (_) {}
    },
  };

  const focusableElements = () => {
    if (!(dialog instanceof HTMLElement)) return [];
    return Array.from(
      dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element instanceof HTMLElement && !element.hasAttribute('hidden'));
  };

  const celebrate = () => {
    if (!confettiEnabled || !(overlay instanceof HTMLElement)) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    overlay.querySelector('[data-hotstop-member-confetti]')?.remove();
    if (confettiCleanupTimer) window.clearTimeout(confettiCleanupTimer);

    const layer = document.createElement('div');
    layer.className = 'hotstop-member-discount__confetti';
    layer.dataset.hotstopMemberConfetti = '';
    layer.setAttribute('aria-hidden', 'true');

    const palette = ['#ee2a33', '#ffc200', '#5e1717', '#ffffff'];
    const particleCount = window.matchMedia?.('(max-width: 749px)').matches ? 24 : 34;
    const fragment = document.createDocumentFragment();

    for (let index = 0; index < particleCount; index += 1) {
      const piece = document.createElement('i');
      const startX = 16 + Math.random() * 68;
      const drift = -95 + Math.random() * 190;
      const fall = 42 + Math.random() * 40;
      const delay = Math.random() * Math.min(520, confettiDurationMs * 0.18);
      const endVariance = Math.random() * Math.min(280, confettiDurationMs * 0.08);
      const duration = Math.max(650, confettiDurationMs - delay - endVariance);
      const rotation = -240 + Math.random() * 480;
      const width = 6 + Math.random() * 5;
      const height = Math.random() > 0.55 ? width * 0.48 : width * 1.35;

      piece.style.setProperty('--hs-confetti-x', `${startX}%`);
      piece.style.setProperty('--hs-confetti-drift', `${drift}px`);
      piece.style.setProperty('--hs-confetti-fall', `${fall}vh`);
      piece.style.setProperty('--hs-confetti-delay', `${delay}ms`);
      piece.style.setProperty('--hs-confetti-duration', `${duration}ms`);
      piece.style.setProperty('--hs-confetti-rotate', `${rotation}deg`);
      piece.style.setProperty('--hs-confetti-width', `${width}px`);
      piece.style.setProperty('--hs-confetti-height', `${height}px`);
      piece.style.setProperty('--hs-confetti-color', palette[index % palette.length]);
      fragment.appendChild(piece);
    }

    layer.appendChild(fragment);
    overlay.appendChild(layer);

    confettiCleanupTimer = window.setTimeout(() => {
      layer.remove();
      confettiCleanupTimer = 0;
    }, confettiDurationMs + 220);
  };

  const open = ({ markSeen = true } = {}) => {
    if (!authenticated || !(overlay instanceof HTMLElement) || !(dialog instanceof HTMLElement)) return;
    if (!overlay.hidden) return;

    lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    overlay.hidden = false;
    document.body.classList.add('hotstop-member-discount--open');
    document.documentElement.dataset.hotstopB90iPopup = 'open';
    celebrate();

    if (markSeen) storage.set(seenKey, '1');

    window.requestAnimationFrame(() => {
      const focusables = focusableElements();
      (focusables[0] || dialog).focus({ preventScroll: true });
    });
  };

  const close = () => {
    if (!(overlay instanceof HTMLElement) || overlay.hidden) return;
    overlay.hidden = true;
    overlay.querySelector('[data-hotstop-member-confetti]')?.remove();
    if (confettiCleanupTimer) {
      window.clearTimeout(confettiCleanupTimer);
      confettiCleanupTimer = 0;
    }
    document.body.classList.remove('hotstop-member-discount--open');
    document.documentElement.dataset.hotstopB90iPopup = 'closed';
    lastFocusedElement?.focus?.({ preventScroll: true });
  };

  loginLinks.forEach((link) => {
    link.addEventListener('click', () => {
      storage.set(pendingKey, '1');
    });
  });

  openButtons.forEach((button) => {
    button.addEventListener('click', () => open({ markSeen: false }));
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', close);
  });

  document.addEventListener('keydown', (event) => {
    if (!(overlay instanceof HTMLElement) || overlay.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusables = focusableElements();
    if (!focusables.length) {
      event.preventDefault();
      dialog?.focus?.();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  if (authenticated) {
    const loginWasPending = storage.get(pendingKey) === '1';
    if (loginWasPending) storage.remove(pendingKey);

    const shouldAutoOpen = loginWasPending || storage.get(seenKey) !== '1';
    const isThemeEditor = document.documentElement.classList.contains('shopify-design-mode');

    if (shouldAutoOpen && !isThemeEditor) {
      window.setTimeout(() => open({ markSeen: true }), popupDelay);
    }
  } else {
    // Visiting the storefront while signed out resets the session-level popup
    // marker so the reward is shown again after the next successful sign-in.
    storage.remove(seenKey);
  }

  document.documentElement.dataset.hotstopB90iStatus = 'ready';
  document.documentElement.dataset.hotstopB90iAuthenticated = authenticated ? 'true' : 'false';

  window.HotStopMemberDiscount = {
    open: () => open({ markSeen: false }),
    close,
    isAuthenticated: authenticated,
    percent: discountPercent,
  };
})();
